import { useMemo } from "react";
import { AttachmentItem } from "./useAttachments";

export interface TokenInfo {
  estimatedTokens: number;
  maxTokens: number;
  isOverLimit: boolean;
  percentage: number;
  remainingTokens: number;
  isWarning: boolean;
}

interface UseTokenCountParams {
  composerText: string;
  attachments: AttachmentItem[];
  maxTokens: number;
}

// Rough estimation: ~4 characters per token
const CHARS_PER_TOKEN = 4;
const FILE_TOKEN_OVERHEAD = 100; // Base tokens per file

export function useTokenCount({
  composerText,
  attachments,
  maxTokens,
}: UseTokenCountParams): TokenInfo {
  return useMemo(() => {
    // Estimate tokens from text
    const textTokens = Math.ceil(composerText.length / CHARS_PER_TOKEN);

    // Estimate tokens from attachments
    const attachmentTokens = attachments.reduce((sum, attachment) => {
      if (attachment.status !== "ready") return sum;
      // Rough estimate based on file size
      const fileTokens = Math.ceil(attachment.size / CHARS_PER_TOKEN);
      return sum + fileTokens + FILE_TOKEN_OVERHEAD;
    }, 0);

    const estimatedTokens = textTokens + attachmentTokens;
    const isOverLimit = estimatedTokens > maxTokens;
    const percentage = Math.min((estimatedTokens / maxTokens) * 100, 100);
    const remainingTokens = Math.max(maxTokens - estimatedTokens, 0);
    const isWarning = percentage >= 90 && !isOverLimit;

    return {
      estimatedTokens,
      maxTokens,
      isOverLimit,
      percentage,
      remainingTokens,
      isWarning,
    };
  }, [composerText, attachments, maxTokens]);
}
